/**
 * Student theme — l'arène sur la nuit (Brand core V16, « Clair et sombre, au choix »).
 *
 * C'est le versant dans lequel le livre dessine l'élève partout où il le montre en grand
 * (chap. 03, 07, 16, 17). Le socle de la nuit (`nightBase.ts`), plus ce que l'univers de l'élève y
 * devient : **il ne s'inverse pas**. Son orange est une couleur, pas un ton de fond — le panneau
 * « MODE SOMBRE » (chap. 06) dessine le bouton élève tel qu'en clair, à côté du bouton parent
 * devenu blanc. Seule son ÉCRITURE bouge : posée sur la nuit, elle s'éclaircit, comme les encres
 * de progression et d'erreur ; posée sur la face blanche du secondaire, elle reste celle du clair.
 * L'option choisie et le bouton qui détruit sont donc ceux du clair, eux aussi : des aplats.
 */
import { baseTheme } from "./base"
import { nightBaseColors } from "./nightBase"
import { brand, night } from "./tokens/colors"

export const studentDarkTheme = {
    ...baseTheme,
    colors: {
        ...baseTheme.colors,
        ...nightBaseColors,

        role: brand.roleStudentDisplay,
        "role-student-tint": night.roleStudentDisplayTint,
        "role-strong": night.roleStudentInk,
        "role-fill": brand.roleStudentFill,
        "relief-role": brand.roleStudentShade,
        "text-on-role": brand.white,
        "text-on-secondary": brand.roleStudentInk,
        "text-link": night.roleStudentInk,
        "role-selected": brand.roleStudentFill,
        "danger-fill": brand.errorInk,
        "relief-danger": brand.errorInkShade,
        // Sur la nuit, la profondeur se lit à la clarté : la page, la carte, puis ce qui flotte au-dessus
        // du contenu (l'aperçu de Sam), une marche plus clair que la carte qu'il peut couvrir. Il valait
        // la carte : sur l'énoncé, la bulle de Sam se confondait avec la page (1,20) et avec une carte
        // (1,00). Une marche plus haut, elle tient 1,57 contre la page et 1,31 contre la carte.
        "surface-floating": night.raised,
        // Les tintes de statut, aplaties sur la carte où elles se posent (voir `night`) : aplaties sur
        // la page, elles s'y fondaient (1,04 à 1,17).
        successLight: night.progressTintOnCard,
        warningLight: night.alertTintOnCard,
        dangerLight: night.errorTintOnCard,
        // Un statut ou un axe qui s'écrit prend son encre éclaircie, comme le reste de la nuit.
        "text-success": night.progressInk,
        "text-warning": night.alertInk,
        "text-danger": night.errorInk,
        "text-phone": night.tutorInk,
        "text-purple": night.focusedInk,
        // Sur la nuit la lueur n'est pas montée ; le jeton suit pourtant le clair de l'élève. En
        // passant de Sombre à Clair dans « Apparence », la lueur qui monte lit encore ce thème-ci le
        // temps de son premier rendu, et restait jaune jusqu'au rechargement (vu en recette, 19/10).
        glow: brand.roleStudentDisplay,
        // Le voile de ses feuilles, plus sombre, comme en clair (Wajih, 20/10). Sur la nuit il ne gagne
        // presque rien (1,35 → 1,37:1) : la page est déjà presque noire (`contrast.test.ts`).
        scrim: night.scrimDeep,
        // Le liseré clair du bord haut des feuilles vient du socle de la nuit (`nightBase`), comme
        // chez le parent.
    },
} as const
